/**
 * 결제 안내 — "언제 청구서가 오고, 언제까지 내야 하나"
 * ---------------------------------------------------------------------------
 * ## 왜 따로 떼어 놓았나
 * 같은 문구가 두 곳에 필요하다 — **등록 창**(고르는 중)과 **결제 수단 탭**
 * (고른 뒤). 두 벌로 적으면 한쪽만 고쳐져서, 고르기 전에 본 설명과 고른 뒤에
 * 보는 설명이 달라진다. 돈 내는 일정이 그러면 안 된다.
 *
 * ## 숫자를 여기에 적지 않는다
 * 모든 날짜는 `/billing/payment-schedule` 이 준다(공통코드 BILLING_POLICY +
 * 기업별 납기). 화면에 박으면 정책이 바뀔 때 **고객 안내만 옛날 값으로
 * 남는다.**
 */

import { Alert, Descriptions, Typography } from 'antd';
import type { PaymentSchedule } from './paymentSchedule';

const { Text } = Typography;

/** 「매월 1일」처럼 읽히게. 숫자는 서버가 준 것을 그대로 쓴다. */
function dayLabel(n: number): string {
  return `매월 ${n}일`;
}

export function PaymentScheduleNotice({
  schedule,
  method,
}: {
  schedule: PaymentSchedule;
  /** 고르는 중인 수단. 저장된 값과 다를 수 있다(등록 창에서 미리보기) */
  method: 'TRANSFER' | 'CARD';
}) {
  const { invoiceIssueDay, dueDays, taxInvoiceByDay, suspendAfterDays } = schedule;

  if (method === 'CARD') {
    /*
     * 카드는 아직 열리지 않았지만 문구는 미리 만들어 둔다. PG 계약이 끝나
     * CARD_PAYMENT_ENABLED 를 켜면 **이 화면이 그대로 맞는 설명**이 된다.
     * 그때 가서 문구를 새로 쓰면 또 빠뜨린다.
     */
    return (
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="청구서 발송">
          <Text strong>{dayLabel(invoiceIssueDay)}</Text> — 전월 사용분을 정산해 보내 드립니다.
        </Descriptions.Item>
        <Descriptions.Item label="카드 결제">
          청구서 발송 후 <Text strong>{dueDays}일째 되는 날</Text> 등록하신 카드로 자동
          결제됩니다. 따로 하실 일은 없습니다.
        </Descriptions.Item>
        <Descriptions.Item label="영수증">
          카드 결제는 <Text strong>카드매출전표가 증빙</Text>이 되므로 세금계산서를 따로 발행하지
          않습니다.
        </Descriptions.Item>
        <Descriptions.Item label="결제 실패 시">
          한도 초과·카드 정지 등으로 결제가 되지 않으면 안내 메일을 보내 드립니다. 그 뒤에도
          납부가 없으면 <Text strong>납기일로부터 {suspendAfterDays}일</Text> 후 서비스 사용이
          제한됩니다.
        </Descriptions.Item>
      </Descriptions>
    );
  }

  return (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="청구서 발송">
        <Text strong>{dayLabel(invoiceIssueDay)}</Text> — 전월 사용분을 정산해 지정하신 수신자에게
        메일로 보내 드립니다.
      </Descriptions.Item>
      <Descriptions.Item label="입금 기한">
        청구서 발송일로부터 <Text strong>{dueDays}일 이내</Text>. 청구서에 적힌 계좌로 보내 주시면
        담당자가 수납을 기록합니다.
      </Descriptions.Item>
      <Descriptions.Item label="세금계산서">
        사용월의 <Text strong>다음 달 {taxInvoiceByDay}일까지</Text> 발행해 드립니다. 받으실 메일
        주소는 <Text strong>구성원 설정의 「청구 문서 수신」</Text>에서 정한 분들입니다.
      </Descriptions.Item>
      <Descriptions.Item label="미입금 시">
        입금 기한을 <Text strong>{suspendAfterDays}일</Text> 넘기면 서비스 사용이 제한됩니다.
        입금이 확인되면 바로 풀립니다.
      </Descriptions.Item>
    </Descriptions>
  );
}

/** 등록 창과 탭 머리에 함께 쓰는 한 줄 요약. */
export function PaymentScheduleSummary({ schedule }: { schedule: PaymentSchedule }) {
  if (schedule.payMethod === 'CARD') {
    return (
      <Alert
        type="success"
        showIcon
        message="카드 자동결제"
        description={`매월 ${schedule.invoiceIssueDay}일 청구서가 나가고, ${schedule.dueDays}일째 되는 날 등록된 카드로 자동 결제됩니다.`}
      />
    );
  }
  return (
    <Alert
      type="info"
      showIcon
      message="계좌이체"
      description={`매월 ${schedule.invoiceIssueDay}일 청구서가 나가고, ${schedule.dueDays}일 이내에 입금해 주시면 됩니다. 세금계산서는 다음 달 ${schedule.taxInvoiceByDay}일까지 발행됩니다.`}
    />
  );
}
